import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type {
  AlertListResponse,
  ClusterOverviewResponse,
  ExecSessionListResponse,
  NamespaceDetailResponse,
  NamespaceListResponse,
  NodeDetailResponse,
  NodeSummaryListResponse,
  LogSearchResponse,
  ManagedActionResult,
  ManagedDeploymentDetail,
  ManualWorkloadActionRequest,
  ManualWorkloadActionResponse,
  ManualWorkloadKind,
  ManualWorkloadSnapshotResponse,
  ManagedSecretDetail,
  ManagedWorkloadListResponse,
  GitOpsApplicationDetailResponse,
  GitOpsApplicationListResponse,
  PodDetailResponse,
  RangeKey,
  ResourceCatalogResponse,
  ResourceDetailResponse,
  ResourceListResponse,
  ResourceMapRelationType,
  ResourceMapResponse,
  ResourceRecentItem,
  ResourceRecentResponse,
  ResourceSearchResponse,
  ScopeResponse,
  TopologyEdgeSeriesResponse,
  TopologyLayout,
  TopologyNodePosition,
  TopologyResponse,
  WarningEventsResponse,
  WorkloadDetailResponse,
  WorkloadsOverviewResponse,
} from "@k8s-dashboard/contracts";
import { chunkRecentRefs, REF_KEY, type RecentRef } from "@/app/recent";
import { useScopeAuthGeneration } from "./ScopeAuthLifecycle";
import {
  HttpError,
  NamespaceSelectionAcknowledgementError,
  apiGet,
  apiGetNamespaceSelected,
  apiRequest,
  clusterPath,
  currentScenarioParam,
  getAuthGeneration,
  type APIQueryParams,
} from "./client";
import { parseScopeResponse } from "./scope";
import { useDashboardStore } from "@/state/dashboardStore";
import { namespaceSelectionTransport, selectionAuthorized } from "@/state/namespaceSelection";
import { useNamespaceSelectionIdentityReady } from "@/state/namespaceSelectionIdentity";

/**
 * 시간 범위 → 요청 파라미터. custom은 from/to(epoch ms)를 반드시 함께 실어야 하며,
 * 없으면 range만 보냅니다 — `range=custom`이 from/to 없이 나가 서버가 400을 내는 것을
 * 막습니다(서버 timerange.Parse는 from/to가 있으면 그것을 Custom으로 읽습니다). (#58)
 */
function withRange(range: RangeKey, from?: string, to?: string): Record<string, string> {
  return range === "custom" && from && to ? { range, from, to } : { range };
}

/** custom 두 창이 같은 캐시 키를 공유하지 않도록 from/to를 키 꼬리에 붙입니다. 프리셋은 빈 꼬리입니다. */
function rangeKeyTail(range: RangeKey, from?: string, to?: string): readonly string[] {
  return range === "custom" && from && to ? [from, to] : [];
}

function useNamespaceSelectionRequest(legacyKey: "ns" | "namespace" = "ns", requireManageSingleton = false) {
  const identityReady = useNamespaceSelectionIdentityReady();
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const status = useDashboardStore((state) => state.namespaceSelectionStatus);
  const capability = useDashboardStore((state) => state.namespaceSelectionCapability);
  const generation = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const transport = useMemo(
    () => selection ? namespaceSelectionTransport(selection, capability, legacyKey) : undefined,
    [capability, legacyKey, selection],
  );
  const selectedSingleton = selection?.mode === "selected" && selection.names.length === 1;
  const manageSingleton = selectedSingleton &&
    (transport?.mode === "new" || transport?.mode === "legacy-single");
  const enabled = identityReady && status === "ready" && transport !== undefined && transport.mode !== "blocked" &&
    (!requireManageSingleton || manageSingleton);
  const get = useCallback(async <T,>(path: string, params: APIQueryParams, signal?: AbortSignal) => {
    if (!transport || !enabled) throw new NamespaceSelectionAcknowledgementError();
    try {
      return await apiGetNamespaceSelected<T>(path, params, transport, signal);
    } catch (error) {
      if (error instanceof NamespaceSelectionAcknowledgementError && transport.mode === "new") {
        useDashboardStore.getState().markNamespaceSelectionAckLost(generation);
      }
      throw error;
    }
  }, [enabled, generation, transport]);
  return { enabled, generation, identity: transport?.binding ?? "unavailable", get, selectedSingleton, transport };
}

export const queryKeys = {
  scope: (authGeneration: number) => ["scope", authGeneration] as const,
  overview: (clusterId: string, namespace: string, range: RangeKey, from?: string, to?: string) =>
    ["cluster-overview", clusterId, namespace, range, ...rangeKeyTail(range, from, to)] as const,
};

export const gitOpsKeys = {
  list: (clusterId: string, selection: string, generation: number) => ["gitops-applications", clusterId, selection, generation] as const,
  detail: (clusterId: string, uid: string, selection: string, generation: number) => ["gitops-application", clusterId, uid, selection, generation] as const,
};

export function useGitOpsApplications(clusterId: string, enabled: boolean) {
  const request = useNamespaceSelectionRequest("ns");
  return useQuery({
    queryKey: gitOpsKeys.list(clusterId, request.identity, request.generation),
    queryFn: ({ signal }) => request.get<GitOpsApplicationListResponse>(
      `/api/v1/clusters/${encodeURIComponent(clusterId)}/gitops/applications`, { limit: "50" }, signal,
    ),
    enabled: enabled && request.enabled && Boolean(clusterId),
    retry: false,
    refetchInterval: false,
  });
}

export function useGitOpsApplication(clusterId: string, uid: string, enabled: boolean) {
  const request = useNamespaceSelectionRequest("ns");
  return useQuery({
    queryKey: gitOpsKeys.detail(clusterId, uid, request.identity, request.generation),
    queryFn: ({ signal }) => request.get<GitOpsApplicationDetailResponse>(
      `/api/v1/clusters/${encodeURIComponent(clusterId)}/gitops/applications/${encodeURIComponent(uid)}`, {}, signal,
    ),
    enabled: enabled && request.enabled && Boolean(clusterId && uid),
    retry: false,
    refetchInterval: false,
  });
}

export function useScope() {
  const authGeneration = useScopeAuthGeneration();
  const query = useQuery({
    queryKey: queryKeys.scope(authGeneration),
    queryFn: async ({ signal }) => {
      const value = parseScopeResponse(await apiGet<unknown>("/api/v1/scope", {}, signal));
      return value;
    },
    staleTime: 5 * 60 * 1000,
    /* 필수 역할 없음(ADR 0035)은 재시도로 바뀌지 않습니다 — 곧바로 안내 화면을 그립니다. */
    retry: (count, error) => !(error instanceof HttpError && error.body.code === "role_required") && count < 3,
  });
  /* 다시 가져오는 동안 필수 Scope cache 데이터는 계속 그릴 수 있습니다. 다만 현재
     자격이 검증되지 않았거나 새 요청이 실패했으면 cache 응답의 partition으로 저장소를
     열어서는 안 됩니다. */
  const data = query.data && (query.isFetching || query.isError)
    ? { ...query.data, preferencePartition: undefined }
    : query.data;
  return data === query.data ? query : { ...query, data };
}

/* ── 진단 위젯 (k8sdiag ADR-0001) ─────────────────────────────────────── */

/* ── Deployment/Secret 관리 (ADR 0014, #32) ───────────────────────────── */

export const manageKeys = {
  list: (clusterId: string, kind: "deployments" | "secrets", ns: string) =>
    ["manage-list", clusterId, kind, ns] as const,
  detail: (clusterId: string, kind: "deployments" | "secrets", ns: string, name: string) =>
    ["manage-detail", clusterId, kind, ns, name] as const,
  snapshot: (clusterId: string, kind: ManualWorkloadKind, ns: string, name: string) =>
    ["manual-workload-snapshot", clusterId, kind, ns, name] as const,
};

const MAX_INT32 = 2_147_483_647;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, required: readonly string[], optional: readonly string[] = []) {
  return required.every((key) => Object.prototype.hasOwnProperty.call(value, key)) &&
    Object.keys(value).every((key) => required.includes(key) || optional.includes(key));
}

function boundedString(value: unknown, maxBytes: number): value is string {
  return typeof value === "string" && value.length > 0 &&
    new TextEncoder().encode(value).byteLength <= maxBytes && !/[\u0000-\u001f\u007f]/u.test(value);
}

function boundedInteger(value: unknown, max = MAX_INT32): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= max;
}

type ManualTargetExpectation = {
  clusterId: string;
  kind: ManualWorkloadKind;
  namespace: string;
  name: string;
};

function validManualTarget(
  value: unknown,
  expected: Omit<ManualTargetExpectation, "clusterId">,
): value is ManualWorkloadSnapshotResponse["target"] {
  return isRecord(value) && hasOnlyKeys(value, ["kind", "namespace", "name", "uid", "resourceVersion"]) &&
    value.kind === expected.kind && value.namespace === expected.namespace && value.name === expected.name &&
    boundedString(value.uid, 128) && boundedString(value.resourceVersion, 128);
}

function invalidManualResponse(): never {
  throw new Error("Invalid response");
}

function parseManualSnapshot(value: unknown, expected: ManualTargetExpectation): ManualWorkloadSnapshotResponse {
  if (!isRecord(value) || !hasOnlyKeys(value,
    ["version", "clusterId", "generatedAt", "target", "currentReplicas", "strategy"]) ||
      value.version !== 1 || value.clusterId !== expected.clusterId ||
      !boundedString(value.clusterId, 128) || !boundedString(value.generatedAt, 64) ||
      Number.isNaN(Date.parse(value.generatedAt)) || !boundedInteger(value.currentReplicas) ||
      !validManualTarget(value.target, expected) || !isRecord(value.strategy) ||
      !hasOnlyKeys(value.strategy, ["type", "paused"], ["partition"]) ||
      !boundedString(value.strategy.type, 32) || typeof value.strategy.paused !== "boolean" ||
      (value.strategy.partition !== undefined && !boundedInteger(value.strategy.partition))) {
    invalidManualResponse();
  }
  return value as unknown as ManualWorkloadSnapshotResponse;
}

function parseManualActionResponse(value: unknown, request: ManualWorkloadActionRequest): ManualWorkloadActionResponse {
  if (!isRecord(value) || !hasOnlyKeys(value,
    ["version", "action", "outcome", "target", "observedReplicas"], ["desiredReplicas", "requestedRestartAt"]) ||
      value.version !== 1 || value.action !== request.action || !boundedInteger(value.observedReplicas) ||
      !validManualTarget(value.target, request.target) || value.target.uid !== request.target.uid ||
      value.target.resourceVersion !== request.target.resourceVersion ||
      (request.action === "Scale"
        ? value.outcome !== "scaled" || !boundedInteger(value.desiredReplicas, 1000) ||
          value.desiredReplicas !== request.parameters?.replicas || value.requestedRestartAt !== undefined
        : value.outcome !== "restart_requested" || value.desiredReplicas !== undefined ||
          !boundedString(value.requestedRestartAt, 64) || Number.isNaN(Date.parse(value.requestedRestartAt)))) {
    invalidManualResponse();
  }
  return value as unknown as ManualWorkloadActionResponse;
}

function exactManualSelection(namespace: string, generation: number) {
  const state = useDashboardStore.getState();
  return state.namespaceSelectionGeneration === generation && state.namespaceSelectionStatus === "ready" &&
    state.namespaceSelection?.mode === "selected" && state.namespaceSelection.names.length === 1 &&
    state.namespaceSelection.names[0] === namespace;
}

type SnapshotAuthority = {
  canFetch: boolean;
  requestIdentity: string;
  authGeneration: number;
  generation: number;
  namespace: string;
};

function ownsManualSnapshot(current: SnapshotAuthority, requestIdentity = current.requestIdentity) {
  return current.canFetch && current.requestIdentity === requestIdentity &&
    getAuthGeneration() === current.authGeneration && exactManualSelection(current.namespace, current.generation);
}

function unknownManualOutcome(): HttpError {
  return new HttpError(502, {
    code: "unknown_outcome",
    message: "Unknown outcome",
    requestId: "",
  });
}

export function useManagedList(clusterId: string, kind: "deployments" | "secrets", ns: string, enabled: boolean) {
  const selectionRequest = useNamespaceSelectionRequest("ns", true);
  return useQuery({
    queryKey: [...manageKeys.list(clusterId, kind, ns), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<ManagedWorkloadListResponse>(
        `/api/v1/clusters/${encodeURIComponent(clusterId)}/${kind}`,
        {},
        signal,
      ),
    enabled: enabled && selectionRequest.enabled && selectionRequest.selectedSingleton &&
      selectionRequest.transport?.selection.mode === "selected" && selectionRequest.transport.selection.names[0] === ns && Boolean(clusterId),
    retry: retryPolicy,
  });
}

export function useManagedDeployment(clusterId: string, ns: string, name: string, enabled: boolean) {
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const selectionStatus = useDashboardStore((state) => state.namespaceSelectionStatus);
  const selected = selectionStatus === "ready" && selection?.mode === "selected" &&
    selection.names.length === 1 && selection.names[0] === ns;
  const generation = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const query = useQuery({
    queryKey: [...manageKeys.detail(clusterId, "deployments", ns, name), generation],
    queryFn: ({ signal }) =>
      apiGet<ManagedDeploymentDetail>(
        clusterPath(clusterId, "deployments", ns, name),
        {},
        signal,
      ),
    enabled: enabled && selected && Boolean(clusterId && ns && name),
  });
  return selected ? query : { ...query, data: undefined };
}

export function useManagedSecret(clusterId: string, ns: string, name: string, enabled: boolean) {
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const selectionStatus = useDashboardStore((state) => state.namespaceSelectionStatus);
  const selected = selectionStatus === "ready" && selection?.mode === "selected" &&
    selection.names.length === 1 && selection.names[0] === ns;
  const generation = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const query = useQuery({
    queryKey: [...manageKeys.detail(clusterId, "secrets", ns, name), generation],
    queryFn: ({ signal }) =>
      apiGet<ManagedSecretDetail>(
        clusterPath(clusterId, "secrets", ns, name),
        {},
        signal,
      ),
    enabled: enabled && selected && Boolean(clusterId && ns && name),
  });
  return selected ? query : { ...query, data: undefined };
}

/** 관리 write(수정·재배포) 공통 mutation. 성공 시 관련 쿼리를 무효화합니다. */
export function useManageAction(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { method: "PUT" | "POST"; path: string; body?: unknown }) => {
      const selection = useDashboardStore.getState().namespaceSelection;
      const pathNamespace = a.path.split("/")[1] ?? "";
      if (useDashboardStore.getState().namespaceSelectionStatus !== "ready" || selection?.mode !== "selected" ||
          selection.names.length !== 1 || pathNamespace !== selection.names[0]) {
        return Promise.reject(new Error("Manage requires one namespace"));
      }
      return apiRequest<ManagedActionResult>(`/api/v1/clusters/${encodeURIComponent(clusterId)}/${a.path}`, {
        method: a.method,
        ...(a.body !== undefined ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(a.body) } : {}),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manage-detail", clusterId] }),
  });
}

/** ADR 0069 exact-target snapshot. It never repairs selection or polls. */
export function useManualWorkloadSnapshot(
  clusterId: string,
  kind: ManualWorkloadKind,
  ns: string,
  name: string,
  enabled: boolean,
) {
  const qc = useQueryClient();
  const scope = useScope();
  const authGeneration = useScopeAuthGeneration();
  const identityReady = useNamespaceSelectionIdentityReady();
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const selectionStatus = useDashboardStore((state) => state.namespaceSelectionStatus);
  const generation = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const exactSingleton = identityReady && selectionStatus === "ready" && selection?.mode === "selected" &&
    selection.names.length === 1 && selection.names[0] === ns;
  const scopeVerified = Boolean(scope.data && !scope.isFetching && !scope.isError);
  const scopeAuthorized = scopeVerified && selection !== undefined && selectionAuthorized(scope.data, clusterId, selection);
  const canFetch = enabled && exactSingleton && scopeAuthorized && scope.data?.canManageWorkloads === true &&
    Boolean(clusterId && ns && name);
  const ownerIdentity = `${authGeneration}:${workloadScopeIdentity(scopeVerified ? scope.data : undefined, clusterId)}:${generation}:${canFetch ? 1 : 0}`;
  const queryKey = [...manageKeys.snapshot(clusterId, kind, ns, name), ownerIdentity] as const;
  const requestIdentity = JSON.stringify(queryKey);
  const authority = useRef({ canFetch, requestIdentity, authGeneration, generation, namespace: ns });
  authority.current = { canFetch, requestIdentity, authGeneration, generation, namespace: ns };

  useEffect(() => {
    if (!canFetch) void qc.cancelQueries({ queryKey, exact: true });
    return () => {
      void qc.cancelQueries({ queryKey, exact: true }).then(() => qc.removeQueries({ queryKey, exact: true }));
    };
  }, [canFetch, qc, requestIdentity]);

  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const expectedOwner = requestIdentity;
      const current = authority.current;
      if (!ownsManualSnapshot(current, expectedOwner)) {
        throw new NamespaceSelectionAcknowledgementError();
      }
      const value = await apiGet<unknown>(
        clusterPath(clusterId, "manage", "workloads", "v1", kind, ns, name), {}, signal,
      );
      const after = authority.current;
      if (!ownsManualSnapshot(after, expectedOwner)) {
        throw new NamespaceSelectionAcknowledgementError();
      }
      return parseManualSnapshot(value, { clusterId, kind, namespace: ns, name });
    },
    enabled: canFetch,
    retry: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });
  const latestQuery = useRef(query);
  latestQuery.current = query;
  const refetch = useCallback((options?: Parameters<typeof query.refetch>[0]) => {
    const current = authority.current;
    if (!ownsManualSnapshot(current)) {
      return Promise.resolve(latestQuery.current);
    }
    return latestQuery.current.refetch(options);
  }, []);
  return canFetch
    ? { ...query, refetch, requestIdentity, canFetch: true }
    : { ...query, refetch, data: undefined, requestIdentity, canFetch: false };
}

/** ADR 0069 action POST. There is intentionally no legacy URL fallback. */
export function useManualWorkloadAction(
  clusterId: string,
  expectedTarget: { kind: ManualWorkloadKind; namespace: string; name: string },
) {
  const qc = useQueryClient();
  const scope = useScope();
  const authGeneration = useScopeAuthGeneration();
  const identityReady = useNamespaceSelectionIdentityReady();
  const selection = useDashboardStore((state) => state.namespaceSelection);
  const selectionStatus = useDashboardStore((state) => state.namespaceSelectionStatus);
  const generation = useDashboardStore((state) => state.namespaceSelectionGeneration);
  const scopeVerified = Boolean(scope.data && !scope.isFetching && !scope.isError);
  const selectionAuthorizedNow = selection !== undefined && scopeVerified &&
    selectionAuthorized(scope.data, clusterId, selection);
  const expectedSingleton = selection?.mode === "selected" && selection.names.length === 1 &&
    selection.names[0] === expectedTarget.namespace;
  const canMutate = identityReady && selectionStatus === "ready" && selectionAuthorizedNow &&
    expectedSingleton && scope.data?.canManageWorkloads === true;
  const expectedTargetIdentity = `${expectedTarget.kind}:${expectedTarget.namespace}:${expectedTarget.name}`;
  const ownerIdentity = `${authGeneration}:${workloadScopeIdentity(scopeVerified ? scope.data : undefined, clusterId)}:${generation}:${canMutate ? 1 : 0}:${expectedTargetIdentity}`;
  const authority = useRef({ canMutate, ownerIdentity, authGeneration, generation, clusterId });
  const active = useRef<AbortController>();
  authority.current = { canMutate, ownerIdentity, authGeneration, generation, clusterId };

  useEffect(() => () => {
    active.current?.abort(new DOMException("Owner changed", "AbortError"));
    active.current = undefined;
  }, [ownerIdentity]);

  return useMutation({
    mutationFn: async (request: ManualWorkloadActionRequest) => {
      const current = authority.current;
      if (!current.canMutate || current.clusterId !== clusterId || getAuthGeneration() !== current.authGeneration ||
          !exactManualSelection(request.target.namespace, current.generation) ||
          (expectedTarget !== undefined && (request.target.kind !== expectedTarget.kind ||
            request.target.namespace !== expectedTarget.namespace || request.target.name !== expectedTarget.name))) {
        throw new Error("Manual action unavailable");
      }
      const submittedOwner = current.ownerIdentity;
      active.current?.abort(new DOMException("Superseded", "AbortError"));
      const controller = new AbortController();
      active.current = controller;
      try {
        const value = await apiRequest<unknown>(
          clusterPath(clusterId, "manage", "actions", "v1"),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(request),
            signal: controller.signal,
          },
          { authRetry: false },
        );
        if (authority.current.ownerIdentity !== submittedOwner ||
            getAuthGeneration() !== current.authGeneration ||
            !exactManualSelection(request.target.namespace, current.generation)) {
          throw new NamespaceSelectionAcknowledgementError();
        }
        return parseManualActionResponse(value, request);
      } catch (error) {
        if (authority.current.ownerIdentity !== submittedOwner) {
          throw new NamespaceSelectionAcknowledgementError();
        }
        if (error instanceof HttpError && error.status < 500) throw error;
        if (error instanceof NamespaceSelectionAcknowledgementError) throw error;
        throw unknownManualOutcome();
      } finally {
        if (active.current === controller) active.current = undefined;
      }
    },
    retry: false,
    onSuccess: (response) => {
      void qc.invalidateQueries({
        queryKey: manageKeys.snapshot(clusterId, response.target.kind, response.target.namespace, response.target.name),
      });
    },
  });
}

/**
 * Cluster Overview는 **한 번의 요청**으로 화면 전체를 받습니다.
 * 위젯마다 훅을 만들면 초기 로딩에서 N+1이 발생합니다. (이슈 #14 완료 기준)
 *
 * `placeholderData: keepPreviousData` 덕분에 범위/스코프를 바꿔도 화면이 비워지지 않고
 * 값만 교체됩니다. 자동 갱신도 같은 경로를 씁니다.
 */
export function useClusterOverview(args: {
  clusterId: string;
  namespace: string;
  range: RangeKey;
  from?: string;
  to?: string;
  refreshMs: number;
}) {
  const { clusterId, namespace, range, from, to, refreshMs } = args;
  const selectionRequest = useNamespaceSelectionRequest("namespace");
  return useQuery({
    queryKey: [...queryKeys.overview(clusterId, namespace, range, from, to), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<ClusterOverviewResponse>(
        clusterPath(clusterId, "overview"),
        withRange(range, from, to),
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(clusterId),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
    refetchOnWindowFocus: false,
    /* 권한 문제는 재시도해도 달라지지 않습니다. */
    retry: (count, error) => !(error instanceof HttpError || error instanceof NamespaceSelectionAcknowledgementError) && count < 2,
    staleTime: 10_000,
  });
}

export const workloadsOverviewKeys = {
  screen: (clusterId: string, namespace: string, q: string, authorizationIdentity: string) =>
    ["workloads-overview", clusterId, namespace, q, authorizationIdentity] as const,
};

/** Workloads 화면은 한 BFF 요청만 쓰며 자동 재조회는 하지 않습니다. */
export function useWorkloadsOverview(clusterId: string, namespace: string, q: string) {
  const selectionRequest = useNamespaceSelectionRequest();
  const qc = useQueryClient();
  const scope = useScope();
  const authGeneration = useScopeAuthGeneration();
  const scopeRetryGeneration = useRef<number | null>(null);
  const scopeVerified = Boolean(scope.data && !scope.isFetching && !scope.isError);
  const scopeRetrying = scopeRetryGeneration.current === authGeneration && scope.isFetching;
  const scopeFailed = scope.isError || scopeRetrying;
  const scopePending = !scopeFailed && !scopeVerified;
  const authorized = selectionRequest.transport
    ? selectionAuthorized(scopeVerified ? scope.data : undefined, clusterId, selectionRequest.transport.selection)
    : false;
  const scopeIdentity = workloadScopeIdentity(scopeVerified ? scope.data : undefined, clusterId);
  const authorizationIdentity = `${authGeneration}:${scopeIdentity}`;
  const queryKey = [...workloadsOverviewKeys.screen(clusterId, namespace, q, authorizationIdentity), selectionRequest.identity, selectionRequest.generation] as const;
  const enabled = Boolean(clusterId) && scopeVerified && authorized && selectionRequest.enabled;

  /* AppShell은 새 자격의 Scope를 확인하는 동안에도 Outlet을 유지할 수 있습니다.
     이전 관측 결과는 즉시 숨기고 이 화면의 진행 중 요청을 모두 취소합니다. 확인된
     generation/fingerprint는 아래에서 별도 캐시 키를 소유합니다. */
  useEffect(() => {
    if (enabled) return;
    void qc.cancelQueries({ queryKey: ["workloads-overview", clusterId], exact: false });
  }, [clusterId, enabled, qc]);

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      selectionRequest.get<WorkloadsOverviewResponse>(
        clusterPath(clusterId, "workloads"),
        {
          ...(q ? { q } : {}),
        },
        signal,
      ),
    enabled,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 10_000,
    meta: { streamInvalidation: "manual" },
  });
  const latestQuery = useRef(query);
  const currentAuthorization = useRef({ enabled, authGeneration });
  const latestScope = useRef({ authGeneration, failed: scopeFailed, refetch: scope.refetch });
  latestQuery.current = query;
  currentAuthorization.current = { enabled, authGeneration };
  latestScope.current = { authGeneration, failed: scopeFailed, refetch: scope.refetch };
  const refetch = useCallback((options?: Parameters<typeof query.refetch>[0]) => {
    const current = currentAuthorization.current;
    if (!current.enabled || getAuthGeneration() !== current.authGeneration) {
      return Promise.resolve(latestQuery.current);
    }
    return latestQuery.current.refetch(options);
  }, []);
  const retryScope = useCallback(() => {
    const current = latestScope.current;
    if (!current.failed || getAuthGeneration() !== current.authGeneration) {
      return Promise.resolve(undefined);
    }
    scopeRetryGeneration.current = current.authGeneration;
    return current.refetch().then(() => undefined);
  }, []);
  const requestIdentity = JSON.stringify(queryKey);
  const scopeState = {
    authorizationPending: scopePending,
    scopeError: scopeFailed,
    scopeRetrying,
    retryScope,
  };
  return enabled
    ? { ...query, refetch, requestIdentity, ...scopeState, canFetch: true }
    : { ...query, refetch, data: undefined, requestIdentity, ...scopeState, canFetch: false };
}

function workloadScopeIdentity(scope: ScopeResponse | undefined, clusterId: string): string {
  const cluster = scope?.clusters.find((item) => item.id === clusterId);
  if (!cluster) return "unverified";
  const namespaces = cluster.namespaces === "all" ? "all" : [...cluster.namespaces].sort().join("\u001f");
  return `${cluster.id.length}:${cluster.id}|${cluster.accessible ? 1 : 0}|${namespaces.length}:${namespaces}`;
}

/* ══════════════════════════════════════════════════════════════════════════
   Drill-down (이슈 #15) — 화면 하나당 요청 하나. ADR 0002를 그대로 따릅니다.
   ══════════════════════════════════════════════════════════════════════════ */

/** 권한 문제는 재시도해도 결과가 같습니다. 404도 마찬가지입니다. */
const retryPolicy = (count: number, error: unknown) =>
  !(error instanceof NamespaceSelectionAcknowledgementError) &&
  !(error instanceof HttpError && (error.status === 403 || error.status === 404)) && count < 2;

const common = {
  placeholderData: keepPreviousData,
  refetchOnWindowFocus: false,
  staleTime: 10_000,
  retry: retryPolicy,
} as const;

export const drillKeys = {
  nodes: (clusterId: string) => ["nodes", clusterId] as const,
  /**
   * 노드는 UID가 신원입니다(#207). 이름이 같아도 재생성되면 다른 캐시 항목입니다 —
   * 이름만 키로 쓰면 재생성 뒤에도 옛 노드의 응답이 화면에 남습니다.
   */
  node: (clusterId: string, name: string, uid: string, range: RangeKey, from?: string, to?: string) =>
    ["node", clusterId, name, uid, range, ...rangeKeyTail(range, from, to)] as const,
  namespaces: (clusterId: string, range: RangeKey, from?: string, to?: string) =>
    ["namespaces", clusterId, range, ...rangeKeyTail(range, from, to)] as const,
  namespace: (clusterId: string, ns: string, range: RangeKey, from?: string, to?: string) =>
    ["namespace", clusterId, ns, range, ...rangeKeyTail(range, from, to)] as const,
  workload: (
    clusterId: string,
    ns: string,
    kind: string,
    name: string,
    range: RangeKey,
    from?: string,
    to?: string,
    objective?: string,
    sloWindow?: string,
    compareAnchor?: string,
    compareWindow?: string,
    investigation?: string,
    /** 요청한 워크로드 UID(#208 · 선택). 같은 이름의 다른 세대는 **다른 캐시 항목**입니다. */
    uid?: string,
  ) =>
    [
      "workload",
      clusterId,
      ns,
      kind,
      name,
      range,
      ...rangeKeyTail(range, from, to),
      objective ?? "",
      sloWindow ?? "",
      compareAnchor ?? "",
      compareWindow ?? "",
      investigation ?? "",
      uid ?? "",
    ] as const,
  /** Pod는 UID가 신원입니다. 이름이 같아도 재생성되면 다른 캐시 항목입니다. */
  pod: (clusterId: string, ns: string, name: string, uid: string, range: RangeKey, from?: string, to?: string) =>
    ["pod", clusterId, ns, name, uid, range, ...rangeKeyTail(range, from, to)] as const,
};

/**
 * 노드 목록은 경량 요약(`?view=summary`)을 씁니다(#207).
 *
 * 목록에 노드별 Pod 행을 싣지 않으므로 응답이 Pod 수에 비례해 커지지 않습니다.
 * Pod는 노드 상세에서 봅니다. 레거시 응답(`view` 없음)은 서버에 그대로 남아 있어
 * 기존 클라이언트가 깨지지 않습니다.
 */
export function useNodeList(clusterId: string, refreshMs: number) {
  return useQuery({
    ...common,
    queryKey: drillKeys.nodes(clusterId),
    queryFn: ({ signal }) =>
      apiGet<NodeSummaryListResponse>(clusterPath(clusterId, "nodes"), { view: "summary" }, signal),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });
}

/**
 * 노드 상세입니다. `uid`가 없으면 요청하지 않습니다 — 이름만으로 조회하면 같은
 * 이름으로 재생성된 노드의 값을 받을 수 있고, 서버도 uid 없는 요청을 400으로
 * 거절합니다.
 */
export function useNodeDetail(
  clusterId: string,
  name: string,
  uid: string,
  range: RangeKey,
  refreshMs: number,
  from?: string,
  to?: string,
) {
  return useQuery({
    ...common,
    enabled: Boolean(name) && Boolean(uid),
    queryKey: drillKeys.node(clusterId, name, uid, range, from, to),
    queryFn: ({ signal }) =>
      apiGet<NodeDetailResponse>(
        clusterPath(clusterId, "nodes", name),
        { uid, ...withRange(range, from, to) },
        signal,
      ),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });
}

export function useNamespaceList(clusterId: string, range: RangeKey, refreshMs: number, from?: string, to?: string) {
  return useQuery({
    ...common,
    queryKey: drillKeys.namespaces(clusterId, range, from, to),
    queryFn: ({ signal }) =>
      apiGet<NamespaceListResponse>(clusterPath(clusterId, "namespaces"), withRange(range, from, to), signal),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });
}

export function useNamespaceDetail(
  clusterId: string,
  ns: string,
  range: RangeKey,
  refreshMs: number,
  from?: string,
  to?: string,
) {
  return useQuery({
    ...common,
    queryKey: drillKeys.namespace(clusterId, ns, range, from, to),
    queryFn: ({ signal }) =>
      apiGet<NamespaceDetailResponse>(
        clusterPath(clusterId, "namespaces", ns),
        withRange(range, from, to),
        signal,
      ),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
    enabled: Boolean(ns),
  });
}

export function useWorkloadDetail(
  clusterId: string,
  ns: string,
  kind: string,
  name: string,
  range: RangeKey,
  refreshMs: number,
  from?: string,
  to?: string,
  /** 등록된 SLO 목표 id와 기간입니다(ADR 0051 D1 — 둘 다 선택). 서버가 준 목록의
      값만 오고, 비면 서버가 기본값을 고릅니다. 화면 범위는 SLO 기간을 바꾸지 않습니다. */
  objective?: string,
  sloWindow?: string,
  /** 보존 리비전 비교 선택자입니다(ADR 0053 D9 — 둘 다 선택).
      `compareAnchor`는 **서버가 발급한 anchorId**이고 revision 문자열이 아닙니다.
      브라우저는 응답 inventory에서 받은 값을 그대로 되돌려 보낼 뿐입니다. */
  compareAnchor?: string,
  compareWindow?: string,
  /** 저장 지표 조사 요청 선택자입니다(ADR 0055 D2). 요청 여부만 고르고 창·질의를 보내지 않습니다. */
  investigation?: string,
  /**
   * 요청한 워크로드 UID입니다(#208 — **선택**).
   *
   * 보내지 않으면 기존 호출자와 **완전히 같은 요청**입니다. 보내면 서버가 캐시
   * 재사용 전에 현재 informer UID와 대조하고 어긋나면 404로 답합니다. 브라우저
   * 값이 신원이 되는 것이 아니라 **서버 신원과 대조되는 필터**입니다.
   */
  uid?: string,
) {
  return useQuery({
    ...common,
    queryKey: drillKeys.workload(clusterId, ns, kind, name, range, from, to, objective, sloWindow, compareAnchor, compareWindow, investigation, uid),
    queryFn: ({ signal }) =>
      apiGet<WorkloadDetailResponse>(
        clusterPath(clusterId, "workloads", kind, name),
        {
          ns,
          ...withRange(range, from, to),
          ...(objective ? { objective } : {}),
          ...(sloWindow ? { sloWindow } : {}),
          ...(compareAnchor ? { compareAnchor } : {}),
          ...(compareWindow ? { compareWindow } : {}),
          ...(investigation ? { investigation } : {}),
          ...(uid ? { uid } : {}),
        },
        signal,
      ),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
    enabled: Boolean(ns && name),
  });
}

export function usePodDetail(
  clusterId: string,
  ns: string,
  name: string,
  uid: string,
  range: RangeKey,
  refreshMs: number,
  from?: string,
  to?: string,
) {
  return useQuery({
    ...common,
    queryKey: drillKeys.pod(clusterId, ns, name, uid, range, from, to),
    queryFn: ({ signal }) =>
      apiGet<PodDetailResponse>(
        clusterPath(clusterId, "pods", name),
        { ns, ...withRange(range, from, to), ...(uid ? { uid } : {}) },
        signal,
      ),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
    enabled: Boolean(ns && name),
  });
}


/* ══════════════════════════════════════════════════════════════════════════
   Logs Explorer (이슈 #16)
   ══════════════════════════════════════════════════════════════════════════ */

export interface LogFilters {
  clusterId: string;
  namespace: string;
  workload: string;
  podUid: string;
  container: string;
  levels: string[];
  q: string;
  range: RangeKey;
  /** 차트 구간 선택으로 좁힌 범위. 없으면 range를 씁니다. */
  from?: number;
  to?: number;
}

export const logKeys = {
  search: (f: LogFilters) =>
    [
      "logs",
      f.clusterId,
      f.namespace,
      f.workload,
      f.podUid,
      f.container,
      [...f.levels].sort().join(","),
      f.q,
      f.range,
      f.from ?? null,
      f.to ?? null,
    ] as const,
};

/**
 * 무한 스크롤. **offset이 아니라 cursor**를 씁니다.
 * 로그가 계속 들어오는 동안 offset을 쓰면 페이지 경계가 밀려 중복·누락이 생깁니다.
 * (이슈 #16 완료 기준)
 */
export function useLogSearch(f: LogFilters) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useInfiniteQuery({
    queryKey: [...logKeys.search(f), selectionRequest.identity, selectionRequest.generation],
    initialPageParam: "" as string,
    queryFn: ({ signal, pageParam }) =>
      selectionRequest.get<LogSearchResponse>(
        clusterPath(f.clusterId, "logs"),
        {
          /* custom은 from/to(아래에서 실림)가 있을 때만 유효합니다. 없으면 range만 보내
             서버 400을 피합니다. from/to는 차트 구간 선택 또는 custom 범위에서 옵니다. (#58) */
          range: f.range === "custom" && !(f.from && f.to) ? "1h" : f.range,
          ...(f.workload ? { workload: f.workload } : {}),
          ...(f.podUid ? { podUid: f.podUid } : {}),
          ...(f.container ? { container: f.container } : {}),
          ...(f.levels.length ? { levels: f.levels.join(",") } : {}),
          ...(f.q ? { q: f.q } : {}),
          ...(f.from ? { from: String(f.from) } : {}),
          ...(f.to ? { to: String(f.to) } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(f.clusterId),
    getNextPageParam: (last) => last.cursor.next ?? undefined,
    /* 필터를 바꾸면 키가 달라집니다. 이전 결과를 새 결과가 올 때까지 유지해야 화면이
       "빈 스켈레톤 → 값"으로 깜빡이지 않습니다 — 다른 화면 집계 쿼리와 같은 관례입니다. (#96) */
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    retry: retryPolicy,
    staleTime: 10_000,
  });
}


/* ══════════════════════════════════════════════════════════════════════════
   Pod Topology · Alerts
   ══════════════════════════════════════════════════════════════════════════ */

export const topoKeys = {
  graph: (clusterId: string, ns: string, range: RangeKey, from?: string, to?: string) =>
    ["topology", clusterId, ns, range, ...rangeKeyTail(range, from, to)] as const,
  series: (clusterId: string, edgeId: string, range: RangeKey, from?: string, to?: string) =>
    ["topology-series", clusterId, edgeId, range, ...rangeKeyTail(range, from, to)] as const,
};

export function useTopology(clusterId: string, ns: string, range: RangeKey, refreshMs: number, from?: string, to?: string) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useQuery({
    ...common,
    placeholderData: undefined,
    queryKey: [...topoKeys.graph(clusterId, ns, range, from, to), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<TopologyResponse>(
        clusterPath(clusterId, "topology"),
        withRange(range, from, to),
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(clusterId),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });
}

/**
 * 공유 배치 저장 (관리자 전용). 성공하면 topology 화면을 다시 조회해
 * 모든 패널이 같은 저장본을 보게 합니다. 빈 positions는 기본 배치로 초기화합니다. (#28)
 */
export function useSaveTopologyLayout(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (positions: TopologyNodePosition[]) =>
      apiRequest<TopologyLayout>(clusterPath(clusterId, "topology", "layout"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ positions }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["topology", clusterId] }),
  });
}

/**
 * 엣지 시계열은 **선택했을 때만** 조회합니다.
 * 그래프를 그릴 때 모든 엣지의 시계열을 미리 받으면 화면 하나에 12번의 조회가 붙습니다.
 * 화면 단위 집계(ADR 0002)의 예외이며, 사용자 조작으로 발생하는 추가 조회입니다.
 */
export function useEdgeSeries(clusterId: string, edgeId: string | null, range: RangeKey, from?: string, to?: string) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useQuery({
    ...common,
    placeholderData: undefined,
    queryKey: [...topoKeys.series(clusterId, edgeId ?? "", range, from, to), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<TopologyEdgeSeriesResponse>(
        clusterPath(clusterId, "topology", "edges", edgeId!, "series"),
        withRange(range, from, to),
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(edgeId),
  });
}

export const alertKeys = {
  list: (clusterId: string, ns: string, range: RangeKey, from?: string, to?: string) =>
    ["alerts", clusterId, ns, range, ...rangeKeyTail(range, from, to)] as const,
};

export function useAlerts(clusterId: string, ns: string, range: RangeKey, refreshMs: number, from?: string, to?: string) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useQuery({
    ...common,
    placeholderData: undefined,
    queryKey: [...alertKeys.list(clusterId, ns, range, from, to), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<AlertListResponse>(
        clusterPath(clusterId, "alerts"),
        withRange(range, from, to),
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(clusterId),
    refetchInterval: refreshMs > 0 ? refreshMs : false,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   Warning Events 화면 (#208)
   --------------------------------------------------------------------------
   조회 전용이고 **폴링하지 않습니다.** 공용 대시보드 `refreshMs`가 0이 아니어도
   이 화면은 interval을 걸지 않습니다 — 갱신은 기존 공용 SSE 하나(StreamInvalidator)가
   clusterId를 품은 query key를 무효화하는 것과 사용자의 명시적 갱신뿐입니다.
   그래서 query key 첫머리에 clusterId가 들어갑니다.

   필터·cursor가 바뀌면 queryKey가 바뀌므로 **늦게 도착한 옛 응답이 새 결과를
   덮어쓸 수 없고**, TanStack이 주는 signal이 이전 요청을 네트워크까지 취소합니다.
   ══════════════════════════════════════════════════════════════════════════ */

export interface WarningEventFiltersInput {
  clusterId: string;
  /** 전역 namespace 선택(앱 상태 · #94). `all`이면 서버가 Scope 전체를 적용합니다. */
  namespace: string;
  range: RangeKey;
  from?: string;
  to?: string;
  q: string;
  reason: string;
  kind: string;
  limit: number;
  cursor: string;
}

export const eventKeys = {
  /**
   * **신원**은 cursor를 뺀 나머지 전부입니다 — cluster·전역 namespace·범위·필터·limit.
   *
   * 이 구분이 필요한 이유: 같은 신원의 다음 페이지를 불러오는 동안에는 앞 페이지를
   * 그려 두는 것이 정상 페이지네이션이지만, **신원이 바뀌면 이전 결과는 다른 질의의
   * 답**입니다. 그것을 새 헤더·새 선택 아래 그려 두면 사용자는 보고 있는 행이 지금
   * 선택의 결과라고 믿습니다.
   */
  identity: (f: WarningEventFiltersInput) =>
    [
      "warning-events",
      f.clusterId,
      f.namespace,
      f.range,
      ...rangeKeyTail(f.range, f.from, f.to),
      f.q,
      f.reason,
      f.kind,
      f.limit,
    ] as const,
  page: (f: WarningEventFiltersInput) => [...eventKeys.identity(f), f.cursor] as const,
};

/**
 * 이전 값을 유지해도 되는지 판정합니다. **신원이 같을 때만** 참입니다.
 *
 * 순수 함수로 떼어 둔 이유: 이 판정이 틀리면 **다른 Scope의 행이 새 선택의
 * 결과처럼** 보입니다. 화면 테스트로는 TanStack 내부를 지나야 하므로, 규칙 자체를
 * 직접 고정할 수 있게 노출합니다.
 */
export function warningEventsPlaceholder<T>(
  f: WarningEventFiltersInput,
  previous: T | undefined,
  previousKey: readonly unknown[] | undefined,
): T | undefined {
  if (previous === undefined || !previousKey) return undefined;
  const want = eventKeys.identity(f);
  /* 이전 키는 신원 + cursor 하나입니다. 길이가 다르면 다른 종류의 키입니다. */
  if (previousKey.length !== want.length + 1) return undefined;
  return want.every((part, i) => previousKey[i] === part) ? previous : undefined;
}

export function useWarningEvents(f: WarningEventFiltersInput) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useQuery({
    queryKey: [...eventKeys.page(f), selectionRequest.identity, selectionRequest.generation],
    queryFn: ({ signal }) =>
      selectionRequest.get<WarningEventsResponse>(
        clusterPath(f.clusterId, "events"),
        {
          ...withRange(f.range, f.from, f.to),
          ...(f.q ? { q: f.q } : {}),
          ...(f.reason ? { reason: f.reason } : {}),
          ...(f.kind ? { kind: f.kind } : {}),
          limit: String(f.limit),
          ...(f.cursor ? { cursor: f.cursor } : {}),
        },
        signal,
      ),
    enabled: selectionRequest.enabled && Boolean(f.clusterId),
    /** **폴링 없음.** 어떤 갱신 주기도 걸지 않습니다. */
    refetchInterval: false,
    refetchOnWindowFocus: false,
    /**
     * 이전 값을 유지하는 범위를 **신원이 같을 때로 한정**합니다.
     *
     * `keepPreviousData`를 그대로 쓰면 cluster·전역 namespace·범위·필터가 바뀌는
     * 전환에서도 **이전 질의의 행과 그 `nextCursor`**가 새 헤더 아래 남습니다.
     * 사용자는 그것이 지금 선택의 결과라고 읽고, 그 상태에서 "다음"을 누르면
     * 이전 질의의 cursor가 새 필터에 붙습니다.
     *
     * cursor만 다른 경우(같은 질의의 다음 페이지)에는 값을 유지해 레이아웃과
     * 초점이 흔들리지 않게 합니다 — 그것은 같은 질의의 연속이기 때문입니다.
     */
    placeholderData: (previous, previousQuery) =>
      warningEventsPlaceholder(f, previous, previousQuery?.queryKey as readonly unknown[] | undefined),
    /* 미지원(central)·권한 없음·잘못된 cursor는 재시도로 바뀌지 않습니다. */
    retry: (count, error) =>
      !(error instanceof NamespaceSelectionAcknowledgementError) &&
      !(error instanceof HttpError && [400, 403, 404, 501, 502].includes(error.status)) && count < 2,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   Resource Explorer (ADR 0018)
   --------------------------------------------------------------------------
   조회 전용입니다. 브라우저는 Kubernetes를 직접 부르지 않고 BFF의 catalog/list/
   detail 세 경로만 씁니다. 목록은 서버 cursor로만 이어보고(offset 없음),
   **폴링하지 않습니다** — 갱신은 사용자가 명시적으로 일으킵니다.
   ══════════════════════════════════════════════════════════════════════════ */

export const resourceKeys = {
  catalog: (clusterId: string, authorizationKey = "") => authorizationKey
    ? ["resource-catalog", clusterId, authorizationKey] as const
    : ["resource-catalog", clusterId] as const,
  list: (clusterId: string, gvr: string, ns: string, namePrefix: string, labelSelector: string, order: string, identity: string) =>
    ["resource-list", clusterId, gvr, ns, namePrefix, labelSelector, order, identity] as const,
  object: (clusterId: string, gvr: string, ns: string, name: string, uid: string, authorizationKey = "") =>
    authorizationKey
      ? ["resource-object", clusterId, gvr, ns, name, uid, authorizationKey] as const
      : ["resource-object", clusterId, gvr, ns, name, uid] as const,
  /* UID·depth·정규화된 관계 필터가 키에 들어갑니다 — 같은 이름의 다른 객체,
     다른 깊이·다른 필터의 그래프가 캐시 항목을 공유하면 낡은 그래프가 새 요청의
     답처럼 보입니다. */
  map: (clusterId: string, gvr: string, ns: string, name: string, uid: string, depth: number, relations: string) =>
    ["resource-map", clusterId, gvr, ns, name, uid, depth, relations] as const,
};

/* 상태(unsupported·forbidden·syncing·unavailable)는 재시도로 바뀌지 않습니다.
   재시도하면 되지 않을 요청을 반복해 BFF와 클러스터에 부하만 더합니다. */
const resourceRetry = (count: number, error: unknown) =>
  !(error instanceof NamespaceSelectionAcknowledgementError) &&
  !(error instanceof HttpError && error.status >= 400 && error.status !== 500) && count < 2;

export function useResourceCatalog(clusterId: string, enabled: boolean, authorizationKey = "") {
  return useQuery({
    queryKey: resourceKeys.catalog(clusterId, authorizationKey),
    queryFn: ({ signal }) =>
      apiGet<ResourceCatalogResponse>(clusterPath(clusterId, "resources"), {}, signal),
    enabled: enabled && Boolean(clusterId),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
    retry: resourceRetry,
  });
}

export type ResourceListFilters = {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  /** 서버 catalog descriptor가 확인한 API scope입니다. 사용자 입력에서 추론하지 않습니다. */
  namespaced: boolean;
  /** "all"이면 서버가 Scope 전체를 적용합니다. */
  namespace: string;
  namePrefix: string;
  labelSelector: string;
  order: "asc" | "desc";
  limit: number;
  /** 브라우저 내부 cache 격리용이며 두 값 모두 API로 보내지 않습니다. */
  preferencePartition: string;
  scopeFingerprint: string;
  maxPages: number;
};

function resourcePath(f: { clusterId: string; group: string; version: string; resource: string }) {
  return clusterPath(f.clusterId, "resources", f.group, f.version, f.resource);
}

/**
 * 서버 keyset cursor로만 이어봅니다. offset 페이징은 만들지 않습니다 (ADR 0003).
 * cursor는 서버가 만든 불투명 문자열이며 클라이언트는 해석하지 않습니다.
 */
export function useResourceList(f: ResourceListFilters, enabled: boolean) {
  const selectionRequest = useNamespaceSelectionRequest();
  return useInfiniteQuery({
    queryKey: resourceKeys.list(
      f.clusterId,
      `${f.group}/${f.version}/${f.resource}`,
      f.namespace,
      f.namePrefix,
      f.labelSelector,
      f.order,
      `${f.preferencePartition}|${f.scopeFingerprint}|${f.namespaced ? "namespaced" : "cluster"}|${selectionRequest.identity}|${selectionRequest.generation}`,
    ),
    initialPageParam: "" as string,
    queryFn: ({ signal, pageParam }) => {
      const params: APIQueryParams = {
        limit: String(f.limit),
        ...(f.namePrefix ? { name: f.namePrefix } : {}),
        ...(f.labelSelector ? { labelSelector: f.labelSelector } : {}),
        ...(f.order === "desc" ? { order: "desc" } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      };
      /* Namespace transport는 namespaced descriptor에만 유효합니다. cluster scope는
         서버가 토큰 Scope를 계속 강제하며, explicit selection을 보내면 400입니다. */
      return f.namespaced
        ? selectionRequest.get<ResourceListResponse>(resourcePath(f), params, signal)
        : apiGet<ResourceListResponse>(resourcePath(f), params, signal);
    },
    getNextPageParam: (last, pages) => pages.length < f.maxPages ? last.nextCursor || undefined : undefined,
    enabled: enabled && selectionRequest.enabled && Boolean(f.clusterId) && Boolean(f.resource),
    refetchOnWindowFocus: false,
    retry: resourceRetry,
    staleTime: 10_000,
  });
}

/**
 * 상세는 **사용자가 항목을 연 순간에만** 조회합니다. 서버가 격리된 client로
 * live GET하고 정제한 YAML만 돌려줍니다 — 값이 아니라 메타만 옵니다.
 */
export function useResourceObject(
  clusterId: string,
  gvr: { group: string; version: string; resource: string },
  target: { namespace: string; name: string; uid: string } | null,
  enabled = true,
  authorizationKey = "",
) {
  const qc = useQueryClient();
  const gvrKey = `${gvr.group}/${gvr.version}/${gvr.resource}`;
  const namespace = target?.namespace ?? "";
  const name = target?.name ?? "";
  const uid = target?.uid ?? "";
  const queryKey = resourceKeys.object(clusterId, gvrKey, namespace, name, uid, authorizationKey);

  /* 인증 Scope가 pending이면 읽기 요청만 끊습니다. cancelQueries는 직전 성공
     데이터를 캐시에 남기므로 같은 partition의 bounded draft는 마운트 상태를 유지합니다. */
  useEffect(() => {
    if (enabled) return;
    void qc.cancelQueries({
      queryKey: resourceKeys.object(clusterId, gvrKey, namespace, name, uid, authorizationKey),
      exact: true,
    });
  }, [authorizationKey, clusterId, enabled, gvrKey, name, namespace, qc, uid]);

  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      apiGet<ResourceDetailResponse>(
        `${resourcePath({ clusterId, ...gvr })}/object`,
        {
          ...(namespace ? { namespace } : {}),
          name,
          uid,
        },
        signal,
      ),
    enabled: enabled && Boolean(clusterId) && Boolean(gvr.resource) && Boolean(name) && Boolean(uid),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: resourceRetry,
  });
}

/* 닫힌 8관계 어휘의 canonical 순위입니다. Record가 ResourceMapRelationType 전수를
   강제하므로 계약 어휘가 바뀌면 여기서 컴파일이 깨집니다. */
const RESOURCE_MAP_RELATION_RANK: Record<ResourceMapRelationType, number> = {
  owner: 0,
  selects: 1,
  routesTo: 2,
  mounts: 3,
  scales: 4,
  protects: 5,
  storage: 6,
  serviceAccount: 7,
};

/** 관계 어휘 전체를 canonical 순서로 담은 불변 배열입니다. UI 체크박스의 정본입니다. */
export const RESOURCE_MAP_RELATIONS: readonly ResourceMapRelationType[] = Object.freeze(
  (Object.keys(RESOURCE_MAP_RELATION_RANK) as ResourceMapRelationType[]).sort(
    (a, b) => RESOURCE_MAP_RELATION_RANK[a] - RESOURCE_MAP_RELATION_RANK[b],
  ),
);

/**
 * 관계 필터를 정규화합니다 — 입력을 변경하지 않고 검증·중복 제거·canonical 정렬만
 * 합니다. 모르는 값이 섞이면 null(fail-closed)입니다. 빈 목록과 8개 전부는 **같은
 * all-relations 표현**(RESOURCE_MAP_RELATIONS)으로 접힙니다 — 순서·중복이 달라도
 * 같은 요청은 같은 캐시 항목을 씁니다.
 */
export function normalizeResourceMapRelations(
  input: readonly ResourceMapRelationType[],
): readonly ResourceMapRelationType[] | null {
  const seen = new Set<ResourceMapRelationType>();
  for (const relation of input) {
    /* 멤버십은 **own property만** 봅니다. `in`은 프로토타입 체인까지 보므로
       "toString"·"constructor"·"__proto__" 같은 이름이 유효한 관계로 통과해
       요청을 성립시킵니다 — 그 이름들은 어휘가 아니라 fail-closed 대상입니다. */
    if (!Object.prototype.hasOwnProperty.call(RESOURCE_MAP_RELATION_RANK, relation)) return null;
    seen.add(relation);
  }
  if (seen.size === 0 || seen.size === RESOURCE_MAP_RELATIONS.length) return RESOURCE_MAP_RELATIONS;
  return Object.freeze(
    [...seen].sort((a, b) => RESOURCE_MAP_RELATION_RANK[a] - RESOURCE_MAP_RELATION_RANK[b]),
  );
}

export type ResourceMapFilters = {
  clusterId: string;
  group: string;
  version: string;
  resource: string;
  /** focus의 위치입니다. cluster 범위 focus면 빈 문자열이고 요청에서 생략됩니다. */
  namespace: string;
  name: string;
  /** focus 신원의 정본입니다. 이름만으로 그래프를 열지 않습니다. (README §5) */
  uid: string;
  /** 적용 깊이(1..4). URL 검증을 통과한 값만 옵니다. */
  depth: number;
  /** 관계 필터(좁히기 전용, 불변). 빈 배열은 전체이며, 훅이 정규화합니다. */
  relations: readonly ResourceMapRelationType[];
};

/**
 * Resource Map 조회 (ADR 0020, #8)
 * --------------------------------------------------------------------------
 * GET 하나뿐입니다. **focus·depth·필터당 요청은 시도 1회**이고, 갱신은 사용자의
 * 명시적 refetch("다시 조회")뿐입니다 — 재시도·재연결·재마운트·창 포커스 어느
 * 것도 추가 요청을 만들지 않습니다. 그래프 조회는 상한이 커서 자동 반복이
 * 곧 부하입니다.
 * 관계 필터가 전체(all-relations 표현)면 relation 파라미터를 아예 싣지 않습니다 —
 * 서버는 부재를 닫힌 어휘 전체로 해석합니다. 부분집합이면 canonical 순서로
 * `relation` 키를 **선택된 관계당 하나씩 반복**해 싣습니다.
 */
export function useResourceMap(f: ResourceMapFilters, enabled: boolean) {
  const relations = normalizeResourceMapRelations(f.relations);
  /* 직접 호출자도 훅 스스로 검증합니다 — 화면 검증에 기대면 다른 호출자가
     서버가 400으로 거절할 요청을 만들 수 있습니다. 하나라도 어긋나면 요청 0회입니다. */
  const valid =
    Boolean(f.clusterId) &&
    Boolean(f.group) &&
    Boolean(f.version) &&
    Boolean(f.resource) &&
    Boolean(f.name) &&
    Boolean(f.uid) &&
    Number.isInteger(f.depth) &&
    f.depth >= 1 &&
    f.depth <= 4 &&
    relations !== null;
  const relationSubset = relations !== null && relations.length < RESOURCE_MAP_RELATIONS.length ? relations : null;
  return useQuery({
    queryKey: resourceKeys.map(
      f.clusterId,
      `${f.group}/${f.version}/${f.resource}`,
      f.namespace,
      f.name,
      f.uid,
      f.depth,
      relations === null ? "invalid" : relations.join(","),
    ),
    queryFn: ({ signal }) =>
      apiGet<ResourceMapResponse>(
        /* 반복 relation 키는 Record 파라미터로 표현할 수 없어 경로 질의로 싣습니다.
           canonical 순서라 같은 부분집합은 언제나 같은 wire 표현입니다. */
        `${resourcePath(f)}/map${
          relationSubset ? "?" + relationSubset.map((r) => `relation=${encodeURIComponent(r)}`).join("&") : ""
        }`,
        {
          name: f.name,
          uid: f.uid,
          ...(f.namespace ? { namespace: f.namespace } : {}),
          depth: String(f.depth),
        },
        signal,
      ),
    enabled: enabled && valid,
    /* 단일 요청/수동 갱신 계약입니다. 실패(500·네트워크 거절 포함)도 시도 1회로
       끝나고, 재마운트·재연결·창 포커스는 캐시가 낡았어도 요청을 만들지 않습니다.
       유일한 갱신 경로는 명시적 refetch입니다. */
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 10_000,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   전역 리소스 검색 · 최근 항목 (ADR 0023)
   --------------------------------------------------------------------------
   Command Palette의 유일한 데이터 원천입니다. Kubernetes를 직접 부르지 않고
   BFF의 search/recent 두 경로만 씁니다. **폴링하지 않습니다** — 사용자가 입력할
   때만, 그것도 디바운스 뒤에 한 번 나갑니다.
   ══════════════════════════════════════════════════════════════════════════ */

/** 서버가 강제하는 질의 길이입니다. UI도 같은 값을 씁니다. */
export const SEARCH_MIN_QUERY = 2;
export const SEARCH_MAX_QUERY = 64;
/** 서버 MaxSearchPageSize와 같은 값입니다. 더 큰 값을 보내면 400입니다. */
export const SEARCH_MAX_RESULTS = 50;

export const searchKeys = {
  search: (clusterId: string, query: string, cursor: string) =>
    ["resource-search", clusterId, query, cursor] as const,
  /**
   * `epoch`는 팔레트를 여는 횟수입니다. 열 때마다 키가 달라지므로 **반드시 다시
   * 물어보고**, 답이 오기 전까지 이전 열기의 캐시된 제목이 렌더되지 않습니다.
   */
  recent: (clusterId: string, refs: string, epoch = 0) => ["resource-recent", clusterId, refs, epoch] as const,
};

/**
 * 입력을 질의어로 정규화합니다.
 *
 * 화면(raw)과 디바운스된 값이 **같은 규칙**으로 정규화되어야 "지금 화면의 입력에
 * 대응하는 결과인가"를 문자열 비교 하나로 판단할 수 있습니다. 한쪽만 trim하면
 * `"pay "`와 `"pay"`가 다른 질의로 보여 낡은 결과가 최신처럼 렌더됩니다.
 */
export function normalizeSearchQuery(raw: string): string {
  return raw.trim().slice(0, SEARCH_MAX_QUERY);
}

/** 질의 자체가 상한을 벗어났는지. 요청을 만들기 전에 UI가 먼저 봅니다. */
export function searchQueryUsable(raw: string): boolean {
  const q = normalizeSearchQuery(raw);
  return q.length >= SEARCH_MIN_QUERY && q.length <= SEARCH_MAX_QUERY;
}

/** 최근 항목 캐시 키의 참조 부분. 훅과 취소 경로가 같은 문자열을 봐야 합니다. */
export function recentRefsKey(refs: readonly RecentRef[]): string {
  return refs.map((r) => `${r.clusterId} ${r.group}/${r.version}/${r.resource}/${r.uid}`).join(",");
}

/** 최근 항목 요청의 경로. 청킹이 실제 request target을 재려면 이 값이 필요합니다. */
export function recentPath(clusterId: string): string {
  return clusterPath(clusterId, "resources", "recent");
}

/**
 * 전역 검색. **namespace 파라미터가 없는 것이 계약입니다** — 범위는 언제나
 * "이 사용자가 볼 수 있는 전부"이고 UI가 넓히거나 좁혀 떠볼 수 없습니다.
 *
 * 취소는 TanStack이 주는 signal을 그대로 넘겨 네트워크까지 전파합니다. 질의어가
 * 바뀌면 queryKey가 바뀌므로 **늦게 도착한 옛 응답이 새 결과를 덮어쓸 수 없습니다.**
 */
export function useResourceSearch(clusterId: string, query: string, enabled: boolean, cursor = "") {
  const q = normalizeSearchQuery(query);
  return useQuery({
    queryKey: searchKeys.search(clusterId, q, cursor),
    queryFn: ({ signal }) =>
      apiGet<ResourceSearchResponse>(
        clusterPath(clusterId, "resources", "search"),
        { q, limit: String(SEARCH_MAX_RESULTS), ...(cursor ? { cursor } : {}) },
        signal,
      ),
    enabled: enabled && Boolean(clusterId) && searchQueryUsable(q),
    refetchOnWindowFocus: false,
    /* 폴링하지 않습니다. 같은 질의어를 다시 열면 짧은 시간 안에는 캐시를 씁니다. */
    staleTime: 15_000,
    retry: resourceRetry,
  });
}

/**
 * 최근 항목 재해석. 제목의 근거는 전부 서버 값입니다.
 *
 * 참조가 많으면 **요청을 나눠** 보냅니다(query string 6KiB 상한). 나눈 순서가
 * 곧 원래 순서이므로 응답을 순서대로 이어 붙이면 브라우저의 최신순이 그대로
 * 보존됩니다. 취소 신호는 **하나를 공유**해 팔레트를 닫으면 남은 요청도 함께 끊깁니다.
 */
export async function fetchRecentResources(
  clusterId: string,
  refs: readonly RecentRef[],
  signal?: AbortSignal,
): Promise<ResourceRecentItem[]> {
  const pathname = recentPath(clusterId);
  /* 청킹이 실패하면 **요청을 하나도 만들지 않습니다.** 상한을 넘는 요청을 쏘아
     414를 받는 것보다 왜 못 보냈는지 오류로 말하는 편이 정직합니다. */
  const chunks = chunkRecentRefs(refs, { pathname, extraParams: currentScenarioParam() });
  /* 참조가 없어도 **한 번은 물어봅니다.** 0건 조회는 계약이 지원하는 형태이고,
     그 응답이 "최근이 비었다"와 "검색이 꺼졌다"를 구분해 줍니다. 안 물어보면
     기능이 없는 배포에서도 그냥 빈 목록으로 보여 사용자가 원인을 오판합니다.
     이 0건 target도 위 청킹이 이미 같은 자로 재고 통과시킨 것입니다. */
  if (chunks.length === 0) chunks.push([]);

  const out: ResourceRecentItem[] = [];
  for (const chunk of chunks) {
    /* 신호는 **모든 덩어리가 공유**합니다 — 팔레트를 닫으면 남은 덩어리도 함께 끊깁니다. */
    if (signal?.aborted) throw signal.reason;
    /* 같은 이름의 파라미터가 반복되므로 apiGet의 Record 시그니처를 쓸 수 없습니다.
       여기서 만드는 문자열이 곧 청킹이 잰 대상입니다. */
    const search = chunk.map((ref) => `${REF_KEY}=${ref}`).join("&");
    const page = await apiGet<ResourceRecentResponse>(
      search ? `${pathname}?${search}` : pathname,
      {},
      signal,
    );
    out.push(...page.items);
  }
  return out;
}

/**
 * 열 때마다 **다시 물어봅니다.** 캐시된 제목을 그대로 보여주면 권한이 사라졌거나
 * 교체된 항목이 옛 제목으로 남습니다. 그래서 `staleTime: 0`이고, 재확인이 도는
 * 동안에는 팔레트가 이전 값을 렌더하지 않습니다(`isFetching`을 봅니다).
 *
 * `epoch`가 키에 들어가므로 다시 열면 이전 열기의 데이터가 캐시로 살아나지 않습니다.
 * 호출부는 **저장소를 다 읽은 뒤에만** `enabled`를 켜야 합니다 — 아직 읽지 않은
 * 빈 목록으로 켜면 0건 probe가 헛나가고 곧 두 번째 질의가 따라붙습니다.
 */
export function useRecentResources(
  clusterId: string,
  refs: readonly RecentRef[],
  enabled: boolean,
  epoch = 0,
) {
  return useQuery({
    /* 참조 목록과 열기 회차가 캐시 키입니다 — 목록이 바뀌거나 다시 열면 다시 해석합니다. */
    queryKey: searchKeys.recent(clusterId, recentRefsKey(refs), epoch),
    queryFn: ({ signal }) => fetchRecentResources(clusterId, refs, signal),
    /* 참조 0건에서도 켭니다. 0건 probe가 기능 가용성을 알려주는 유일한 신호입니다. */
    enabled: enabled && Boolean(clusterId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
    retry: resourceRetry,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   Debug Sessions (ADR 0030) — 전 사용자 세션 조회·강제 종료 (관리)
   --------------------------------------------------------------------------
   폴링하지 않습니다. 목록은 마운트·수동 새로고침·강제 종료 성공 후에만 갱신합니다.
   범위(자기 세션 vs platform.admin 전체)는 서버가 Scope로 정합니다.
   ══════════════════════════════════════════════════════════════════════════ */

export const execSessionKeys = { list: ["exec-sessions"] as const };

export function useExecSessions(enabled: boolean) {
  return useQuery({
    queryKey: execSessionKeys.list,
    queryFn: ({ signal }) => apiGet<ExecSessionListResponse>("/api/v1/exec-sessions", {}, signal),
    enabled,
    staleTime: 5_000,
    refetchOnWindowFocus: false,
    /* 권한·404(레지스트리 없음)는 재시도해도 달라지지 않습니다. */
    retry: (count, error) => !(error instanceof HttpError) && count < 2,
  });
}

export function useTerminateExecSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/api/v1/exec-sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),
    /* 표식은 즉시 서지만 실제 종료는 처리 replica의 다음 갱신 주기입니다. 목록을 다시 조회해
       "종료 중"에서 "종료됨"으로 넘어가는 것을 사용자가 새로고침으로 확인하게 합니다. */
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: execSessionKeys.list });
      // Pod 카드의 "내 세션" 요약은 진입 화면 응답에 실립니다(ADR 0030 결정 7).
      void qc.invalidateQueries({ queryKey: ["exec-session"] });
    },
  });
}
